import { useAuth0 } from '@auth0/auth0-react';
import { Loader } from '@dynamon-as/ui';
import { FC, useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router';
import { useOrgApi } from '@shared/services';
import { saveRedirectPath } from '../utils/redirectStorage';
import { getAuth0Config } from '../constants';
import type { ProtectedRouteProps } from '../types';
import type { Org } from '@shared/models/requestTypes';

/**
 * Default loader component to display while checking authentication.
 * Uses @dynamon-as/ui Loader as default, but can be overridden.
 */
const DEFAULT_LOADER = (): React.ReactElement => <Loader isLoading />;

/**
 * Internal component that wraps the Outlet with organization context.
 * For single-tenant applications, the organization is static.
 */
const ProtectedRouteContent: FC = (): React.ReactElement => {
    const [org, setOrg] = useState<Org>();
    const { get: getOrg } = useOrgApi();
    const { getAccessTokenSilently, loginWithRedirect } = useAuth0();
    const location = useLocation();

    useEffect((): void => {
        const fetchOrg = async (): Promise<void> => {
            try {
                // Try to get a token first to ensure we're authenticated
                await getAccessTokenSilently();
                const orgData = await getOrg();
                setOrg(orgData);
            } catch {
                // Token is not valid, redirect to login
                const fullPath = `${location.pathname}${location.search}${location.hash}`;
                saveRedirectPath(fullPath);
                const config = getAuth0Config();
                loginWithRedirect({
                    authorizationParams: {
                        scope: 'openid profile email',
                        redirect_uri: config.authorizationParams.redirect_uri,
                    },
                });
            }
        };
        void fetchOrg();
    }, [getOrg, getAccessTokenSilently, location, loginWithRedirect]);
    return <Outlet context={{ org }} />;
};

/**
 * Custom authentication required component that saves the redirect path
 * before triggering Auth0 login.
 */
const AuthenticationRequired: FC<{ children: React.ReactNode }> = ({
    children,
}): React.ReactElement => {
    const { isAuthenticated, isLoading, loginWithRedirect } = useAuth0();
    const location = useLocation();

    useEffect((): void => {
        if (!isAuthenticated && !isLoading) {
            // User is not authenticated - save current path and redirect to login
            const fullPath = `${location.pathname}${location.search}${location.hash}`;
            saveRedirectPath(fullPath);

            // Get the redirect URI from Auth0 config
            const config = getAuth0Config();

            // Trigger Auth0 login with explicit redirect_uri
            loginWithRedirect({
                authorizationParams: {
                    scope: 'openid profile email',
                    redirect_uri: config.authorizationParams.redirect_uri,
                },
            });
        }
    }, [isAuthenticated, isLoading, location, loginWithRedirect]);

    if (isLoading) {
        return <DEFAULT_LOADER />;
    }

    if (!isAuthenticated) {
        return <DEFAULT_LOADER />;
    }

    return <>{children}</>;
};

/**
 * Protected route component that requires authentication.
 *
 * This component wraps its children with authentication check,
 * ensuring that only authenticated users can access the protected content.
 *
 * For use with react-router, it passes the organization to child components
 * via the Outlet context, maintaining compatibility with the existing layout.
 *
 * @param props - Component props including children and optional LoaderComponent
 * @returns Protected content or loading indicator
 *
 * @example
 * ```tsx
 * <ProtectedRoute />  // Uses Outlet for children and provides org context
 * ```
 *
 * @example
 * ```tsx
 * <ProtectedRoute LoaderComponent={CustomSpinner}>
 *   <AdminPanel />
 * </ProtectedRoute>
 * ```
 */
export const ProtectedRoute: FC<ProtectedRouteProps> = ({
    children,
    // LoaderComponent is accepted for API compatibility but not used in current implementation
    // Loading state is handled internally by AuthenticationRequired component
    LoaderComponent = DEFAULT_LOADER,
}): React.ReactElement => {
    // Create the content to be protected
    const content = children ? (
        <AuthenticationRequired>{children}</AuthenticationRequired>
    ) : (
        <AuthenticationRequired>
            <ProtectedRouteContent />
        </AuthenticationRequired>
    );

    // Suppress unused variable warning - LoaderComponent is kept for API compatibility
    void LoaderComponent;

    return <>{content}</>;
};

export default ProtectedRoute;
